import { normalizeStructureKey } from 'features/System/systemStructure/systemStructure.utils';
import type { DeviceType, SystemType } from 'types/devices.types';

export type LicensePool = 'virtual' | 'camera' | 'device';
export type SystemLicenseClass = 'basic' | 'advanced';

// 'CW' is not a member of the SystemType union but has its own SystemTypeForm.json layout, so
// the backend does return it. Only its casing is unconfirmed, hence the normalized compare
// below: getSystemLicenseClass falls back to 'advanced', so a casing mismatch here silently
// bills a license instead of failing loudly.
const BASIC_SYSTEM_TYPES: readonly (SystemType | 'CW')[] = ['USGS', 'CW'];
const ADVANCED_SYSTEM_TYPES: readonly SystemType[] = ['Flood', 'Exacq Vision'];
const TECHBRIDGE_DEVICE_TYPES: readonly DeviceType[] = [
  'Technology Bridge_Siren/Giant Voice',
  'Technology Bridge_Flashing Lights',
];
// Systems whose devices cost nothing. Unlike BASIC_SYSTEM_TYPES this is about the *device*
// pool, and CW is deliberately absent: whether CW devices are free is still an open question.
const VIRTUAL_SYSTEM_TYPES: readonly SystemType[] = ['USGS'];
const VIRTUAL_DEVICE_TYPES: readonly DeviceType[] = ['Manual Road Closure', 'Buoy Conditions'];
const CAMERA_SYSTEM_TYPES: readonly SystemType[] = ['Exacq Vision'];
const CAMERA_DEVICE_TYPES: readonly DeviceType[] = ['Overwatch VMS'];

const matchesSystemType = (
  systemTypes: readonly string[],
  systemType?: SystemType | string | null,
): boolean =>
  systemTypes.some(
    (candidate) => normalizeStructureKey(candidate) === normalizeStructureKey(systemType ?? ''),
  );

/**
 * Resolves whether a system consumes an advanced system license.
 *
 * RescAlert Devices systems are the only conditional case: they become advanced once a
 * TechBridge device is present. Note that `deviceTypes` can only ever be resolved per system
 * *type*, not per system instance - connected devices carry `system_type` but no id linking
 * them to one specific integration, so every RescAlert system in a workplace necessarily
 * shares the same answer. See getLicenseSystems in ../utils/license.utils.
 *
 * TODO: narrow this to a true per-instance check once the devices endpoint returns the id of
 * the system a device belongs to.
 *
 * @param systemType - The system type to classify.
 * @param deviceTypes - Device types present for this system type in the workplace.
 * @returns 'basic' when no system license is required, otherwise 'advanced'.
 */
export const getSystemLicenseClass = (
  systemType?: SystemType | string | null,
  deviceTypes: readonly (DeviceType | string)[] = [],
): SystemLicenseClass => {
  if (matchesSystemType(BASIC_SYSTEM_TYPES, systemType)) return 'basic';
  if (matchesSystemType(ADVANCED_SYSTEM_TYPES, systemType)) return 'advanced';
  if (matchesSystemType(['RescAlert Devices'], systemType)) {
    return deviceTypes.some((deviceType) =>
      TECHBRIDGE_DEVICE_TYPES.includes(deviceType as DeviceType),
    )
      ? 'advanced'
      : 'basic';
  }

  return 'advanced';
};

/**
 * Resolves which license pool a system/device type combination draws from.
 *
 * @param systemType - The selected system type, if any.
 * @param deviceType - The selected device type, if any.
 * @returns 'virtual' (no license cost), 'camera', or 'device' (general license pool).
 */
export const getLicensePool = (
  systemType?: SystemType | string | null,
  deviceType?: DeviceType | string | null,
): LicensePool => {
  if (
    matchesSystemType(VIRTUAL_SYSTEM_TYPES, systemType) ||
    VIRTUAL_DEVICE_TYPES.includes(deviceType as DeviceType)
  ) {
    return 'virtual';
  }
  if (
    matchesSystemType(CAMERA_SYSTEM_TYPES, systemType) ||
    CAMERA_DEVICE_TYPES.includes(deviceType as DeviceType)
  ) {
    return 'camera';
  }
  return 'device';
};